import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CheckCircle2, Loader2, MailWarning } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { LanguageSwitcher } from '@/components/ui/language-switcher';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';

type Phase = 'checking' | 'done' | 'invalid' | 'nolink';

/**
 * POS-I185: opened from the link in the verification e-mail. The token is read from the address once and
 * then removed from the address bar (history, screenshots), and the page asks the browser not to send a
 * referrer. The link works once; a second visit shows the "expired or already used" sentence.
 */
export function VerifyEmailPage() {
  const { t } = useTranslation('auth');
  const token = useRef<string>(new URLSearchParams(window.location.search).get('token') ?? '');
  const started = useRef(false);
  const [phase, setPhase] = useState<Phase>(token.current ? 'checking' : 'nolink');
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const refreshPermissions = useAuthStore((s) => s.refreshPermissions);

  useEffect(() => {
    window.history.replaceState(null, '', window.location.pathname);
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
    return () => { meta.remove(); };
  }, []);

  useEffect(() => {
    if (!token.current || started.current) return;
    started.current = true; // React strict mode runs effects twice; the link must be used once
    api
      .post('/auth/verify-email', { token: token.current })
      .then(() => {
        token.current = '';
        setPhase('done');
        // the banner of a signed-in owner disappears at once
        void refreshPermissions();
      })
      .catch(() => setPhase('invalid'));
  }, [refreshPermissions]);

  const target = isAuthenticated ? '/' : '/login';

  return (
    <Card className="border-0 shadow-xl glass-card animate-scale-in">
      <CardHeader className="space-y-3 pb-4 text-center">
        <div className="mx-auto">
          {phase === 'checking' && <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden />}
          {phase === 'done' && <CheckCircle2 className="h-8 w-8 text-success-text" aria-hidden />}
          {(phase === 'invalid' || phase === 'nolink') && <MailWarning className="h-8 w-8 text-warning-text" aria-hidden />}
        </div>
        <CardTitle className="text-2xl font-bold">{t('verifyPageTitle')}</CardTitle>
        <CardDescription role={phase === 'invalid' ? 'alert' : 'status'}>
          {phase === 'checking' && t('verifyChecking')}
          {phase === 'done' && t('verifyDone')}
          {phase === 'invalid' && t('verifyInvalid')}
          {phase === 'nolink' && t('verifyInvalidLink')}
        </CardDescription>
      </CardHeader>
      {phase !== 'checking' && (
        <CardFooter className="flex flex-col gap-3 pt-2">
          <Link to={target} className="w-full">
            <Button type="button" className="w-full h-11">{phase === 'done' ? t('verifyContinue') : t('signIn')}</Button>
          </Link>
          <LanguageSwitcher size="sm" />
        </CardFooter>
      )}
    </Card>
  );
}
